import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../auth/AuthProvider';
import { COLORS, FONTS } from '../../styles/tokens';
import { AppIcon } from '../../components/AppIcon';

interface DeployedApp {
  slug: string;
  name: string;
  build_status: string;
  deploy_status: string;
  app_url: string;
  owner_email: string;
  category: string;
  current_version: number;
  created_at: string;
  icon_url: string;
}

/** List of all deployed PaaS apps */
export default function DeployedApps(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const [apps, setApps] = useState<DeployedApp[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/apps/deployed', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setApps(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const headerStyle: React.CSSProperties = {
    padding: '10px 16px', textAlign: 'left', fontFamily: FONTS.heading,
    fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase',
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`,
  };

  const blueBtnStyle: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
    textTransform: 'uppercase', cursor: 'pointer',
  };

  const statusColors: Record<string, string> = {
    running: '#4ADE80', built: '#4ADE80', building: '#FBBF24', deploying: '#FBBF24',
    pending: '#6B7280', stopped: '#6B7280', build_failed: '#EF4444', deploy_failed: '#EF4444',
  };

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          Deployed Apps
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/apps/deploy')} style={blueBtnStyle}>Deploy New</button>
          <button onClick={() => navigate('/apps')} style={{
            ...blueBtnStyle, background: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSecondary,
          }}>All Apps</button>
        </div>
      </div>

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading...</p>
      ) : apps.length === 0 ? (
        <div style={{
          padding: '40px', textAlign: 'center', borderRadius: '10px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
        }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '14px', color: colors.textSecondary }}>
            No deployed apps yet. Click "Deploy New" to get started.
          </p>
        </div>
      ) : (
        <div className="vx-table-wrap" style={{ borderRadius: '10px', border: `1px solid ${colors.border}`, overflowX: 'auto' }}>
          <div className="vx-desktop-only">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt }}>
                  <th style={headerStyle}>Name</th>
                  <th style={headerStyle}>Slug</th>
                  <th style={headerStyle}>Build</th>
                  <th style={headerStyle}>Deploy</th>
                  <th style={headerStyle}>Version</th>
                  <th style={headerStyle}>Owner</th>
                </tr>
              </thead>
              <tbody>
                {apps.map(app => (
                  <tr
                    key={app.slug}
                    onClick={() => navigate(`/apps/deployed/${app.slug}`)}
                    style={{
                      cursor: 'pointer', borderBottom: `1px solid ${colors.border}`,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '12px 16px', fontFamily: FONTS.body, fontSize: '13px', fontWeight: 600, color: colors.text }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                        <AppIcon icon={app.icon_url ?? ''} name={app.name} isDark={isDark} size={28} />
                        {app.name}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '12px', color: colors.textSecondary }}>
                      {app.slug}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StatusDot status={app.build_status} color={statusColors[app.build_status] ?? '#6B7280'} />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <StatusDot status={app.deploy_status} color={statusColors[app.deploy_status] ?? '#6B7280'} />
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>
                      v{app.current_version}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>
                      {app.owner_email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="vx-mobile-only">
            {apps.map(app => (
              <button
                key={app.slug}
                type="button"
                onClick={() => navigate(`/apps/deployed/${app.slug}`)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  width: '100%', textAlign: 'left', padding: '14px 16px',
                  border: 'none', borderBottom: `1px solid ${colors.border}`,
                  background: 'transparent', cursor: 'pointer',
                }}
              >
                <AppIcon icon={app.icon_url ?? ''} name={app.name} isDark={isDark} size={36} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: FONTS.body, fontSize: '14px', fontWeight: 600, color: colors.text }}>
                    {app.name}
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                    {app.slug} · v{app.current_version}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    <StatusDot status={app.build_status} color={statusColors[app.build_status] ?? '#6B7280'} />
                    <StatusDot status={app.deploy_status} color={statusColors[app.deploy_status] ?? '#6B7280'} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status, color }: { status: string; color: string }): React.JSX.Element {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '2px 10px', borderRadius: '12px',
      backgroundColor: `${color}20`, fontFamily: FONTS.body, fontSize: '11px', fontWeight: 600, color,
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: color }} />
      {status.replace('_', ' ')}
    </span>
  );
}
